import {
  CollectedDataStatus,
  collectedDataStepsTitle,
  CollectedDataStepType,
} from 'src/constants/collectedData'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'

const SHOW_DOWNLOAD_SELFIE_BUTTON_PARTNER_IDS = [
  '2fc98fcb-debc-4abd-96fd-496b1b8401af',
  '70e4d369-e0db-43cd-8f63-ebc8a98f6abe',
]

/**
 * Whether the step has data to show (for "show details" / review). Used by visibility and step item state.
 */
export function getHasStepData(
  collectedData: CollectedData,
  contractorDetails: ContractorDetails,
  partnerId: string | null | undefined,
  stepStatus: CollectedDataStatus,
): boolean {
  const stepType = collectedData.collectDataStep.type
  const hasDataStatus = !!collectedData.dataStatus
  const hasStitchState = !!contractorDetails.stitchVendorState
  const hasFormItems = !!(collectedData as { metadata?: { formItems?: unknown[] } })?.metadata
    ?.formItems
  const isPartnerWithSelfie = SHOW_DOWNLOAD_SELFIE_BUTTON_PARTNER_IDS.includes(partnerId ?? '')
  const stepStatusIsNotCreated = stepStatus !== CollectedDataStatus.Created

  return (
    (stepType === CollectedDataStepType.Training && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.InsuranceBeneficiary && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.VaccinationInformation && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.VehicleAndInsurance && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.BackgroundCheck && hasDataStatus) ||
    (stepType === CollectedDataStepType.StitchPayment && hasStitchState) ||
    (stepType === CollectedDataStepType.Form && hasFormItems) ||
    (stepType === CollectedDataStepType.BusinessEntity && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.InsuranceCoverage && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.DriverLicense && isPartnerWithSelfie) ||
    (stepType === CollectedDataStepType.MCNumber && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.DOTNumber && stepStatusIsNotCreated) ||
    (stepType === CollectedDataStepType.PIICollection && stepStatusIsNotCreated)
  )
}

export const clientSortCollectedData = (
  collectedData: CollectedData[],
  showWaitingForApprovalStepsFirst?: boolean,
): CollectedData[] => {
  collectedData.sort((a, b) => {
    if (a.collectDataStep.order && b.collectDataStep.order) {
      return a.collectDataStep.order > b.collectDataStep.order ? 1 : -1
    }

    if (a.collectDataStep.type === CollectedDataStepType.InPersonMeeting) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.InPersonMeeting) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.Questionnaire) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.Questionnaire) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.DriverLicense) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.DriverLicense) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.VehicleAndInsurance) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.VehicleAndInsurance) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.InsuranceAmountDeclaration) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.InsuranceAmountDeclaration) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.VehicleRegistration) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.VehicleRegistration) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.VaccinationInformation) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.VaccinationInformation) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.BackgroundCheck) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.BackgroundCheck) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.PaymentMethod) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.PaymentMethod) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.StitchPayment) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.StitchPayment) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.InsuranceBeneficiary) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.InsuranceBeneficiary) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.SignContract) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.SignContract) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.MvrUpload) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.MvrUpload) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.BusinessEntity) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.BusinessEntity) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.MCNumber) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.MCNumber) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.DOTNumber) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.DOTNumber) {
      return 1
    }

    if (a.collectDataStep.type === CollectedDataStepType.PIICollection) {
      return -1
    } else if (b.collectDataStep.type === CollectedDataStepType.PIICollection) {
      return 1
    }

    return 0
  })

  if (showWaitingForApprovalStepsFirst) {
    collectedData.sort((a, b) => {
      if (a.status === CollectedDataStatus.WaitingForApproval) {
        return -1
      } else if (b.status === CollectedDataStatus.WaitingForApproval) {
        return 1
      }

      return 0
    })
  }

  return collectedData
}

/**
 * True when the step is in a "recollecting" state (user or system is re-submitting):
 * either isRecollecting flag is set, or status is Created with a decline reason (re-submit after decline).
 */
export function isStepRecollecting(collectedData: CollectedData): boolean {
  return (
    !!collectedData.isRecollecting ||
    (collectedData.status === CollectedDataStatus.Created &&
      !!collectedData.reviewMetadata?.declineReason)
  )
}

export const getCollectDataStepTitle = (collectedData: CollectedData): string => {
  let title = collectedDataStepsTitle[collectedData.collectDataStep.type]

  if (
    collectedData.collectDataStep.type === CollectedDataStepType.Form &&
    collectedData?.collectDataStep?.label
  ) {
    title = collectedData.collectDataStep.label
  }

  if (
    collectedData.collectDataStep.type === CollectedDataStepType.SignContract &&
    collectedData?.collectDataStep?.label !== CollectedDataStepType.SignContract
  ) {
    title = `${title} - ${collectedData.collectDataStep.label}`
  }

  if (collectedData.collectDataStep.type === CollectedDataStepType.InsuranceCoverage) {
    title = `${title} - ${collectedData.collectDataStep.metadata?.insurancePolicyProduct?.insuranceProduct?.name}`
  }

  if (collectedData.collectDataStep.type === CollectedDataStepType.LmsTraining) {
    title = `${title} - ${collectedData.collectDataStep.metadata?.lmsTrainingName}`
  }

  if (
    collectedData.collectDataStep.type === CollectedDataStepType.VehicleAndInsurance &&
    collectedData.collectDataStep.metadata &&
    'disableInsuranceUpload' in collectedData.collectDataStep.metadata &&
    collectedData.collectDataStep.metadata?.disableInsuranceUpload
  ) {
    title = 'Vehicle Information'
  }

  return title
}

export const getOnboardingTableStepTitle = (stepType: string): string =>
  // @ts-ignore
  collectedDataStepsTitle[stepType] || stepType
